import { axiosDelete, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import { z } from 'zod';
import { queryKeyMap } from '../../../../_utils/query-keys';
import { PaymentContractMutationParamsSchema } from '../../../_models/payment-contract';

export const useCreatePaymentContract = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof PaymentContractMutationParamsSchema>,
    ) => {
      const { projectId, paymentGroupId, paymentId, ...payload } =
        PaymentContractMutationParamsSchema.parse({
          ...values,
          isOthers: !!values.isOthers,
          amountBreakdown: values.isOthers ? [] : values.amountBreakdown,
        });
      const res = await axiosPost(
        `/api/projects/${projectId}/payment-groups/${paymentGroupId}/payments/${paymentId}/payment-contracts/`,
        payload,
      );
      await Promise.all([
        invalidate(queryKeyMap.paymentGroups(projectId)),
        invalidate(queryKeyMap.contractorPayments(projectId)),
        invalidate(queryKeyMap.paymentGraphData(projectId)),
      ]);
      return res;
    },
  });
};

export const useUpdatePaymentContract = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof PaymentContractMutationParamsSchema> & {
        id: string;
      },
    ) => {
      const { id, projectId, paymentGroupId, paymentId, ...payload } =
        PaymentContractMutationParamsSchema.extend({
          id: z.string().uuid(),
        }).parse({
          ...values,
          isOthers: !!values.isOthers,
          amountBreakdown: values.isOthers ? [] : values.amountBreakdown,
        });
      const res = await axiosPatch(
        `/api/projects/${projectId}/payment-groups/${paymentGroupId}/payments/${paymentId}/payment-contracts/${id}/`,
        payload,
      );
      await Promise.all([
        invalidate(queryKeyMap.paymentGroups(projectId)),
        invalidate(queryKeyMap.contractorPayments(projectId)),
        invalidate(queryKeyMap.paymentGraphData(projectId)),
      ]);
      return res;
    },
  });
};

export const useDeletePaymentContract = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      id,
      projectId,
      paymentGroupId,
      paymentId,
    }: {
      id: string;
      projectId: string;
      paymentGroupId: string;
      paymentId: string;
    }) => {
      const res = await axiosDelete(
        `/api/projects/${projectId}/payment-groups/${paymentGroupId}/payments/${paymentId}/payment-contracts/${id}/`,
      );
      await Promise.all([
        invalidate(queryKeyMap.paymentGroups(projectId)),
        invalidate(queryKeyMap.contractorPayments(projectId)),
        invalidate(queryKeyMap.paymentGraphData(projectId)),
      ]);
      return res;
    },
  });
};
